// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicket } from "@/types";
import { formatDateTime } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TySupportTicketDetails = { ticket: TySupportTicket; onHide?: () => void };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/** The same three tones the tile uses. Only solved is green. */
const TONES: Record<TySupportStatus, "reserved" | "accent" | "available"> = {
    pending: "reserved",
    contacted: "accent",
    solved: "available",
};

/**
 * One request, whole: what was asked, and what we said back.
 *
 * **It exists because the list became a grid.** A ticket is prose, and prose in a third-width tile
 * next to five other people's prose is unreadable — so the tile carries the subject and a badge, and
 * everything that has to be read in full is read here, on one column wide enough for a paragraph.
 *
 * Nothing here is editable. A company writes a ticket once; the only thing it can still do to it is
 * take it off its own list, and that is a footer action rather than a field.
 */
export default function SupportTicketPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { ticket, onHide } = (details ?? {}) as TySupportTicketDetails;

    const author = typeof ticket.client === "object" && ticket.client ? ticket.client : null;

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.support.detailsTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <div className="support-ticket__head">
                    <h3 className="support-ticket__subject">{ticket.subject}</h3>
                    <StatusChip size="sm" tone={TONES[ticket.status]} label={t.support.statuses[ticket.status]} />
                </div>

                <div className="support-ticket__who">
                    {author && <Avatar name={author.name} picture={author.picture ?? null} size={28} rounded />}
                    <span className="support-ticket__by">{author?.name ?? ""}</span>
                    <span className="support-ticket__when">{formatDateTime(ticket.createdAt, lang)}</span>
                </div>

                {/* Their own words, as they typed them — line breaks and all. */}
                <p className="support-ticket__message">{ticket.message}</p>

                {ticket.reply ? (
                    <section className="support-ticket__reply">
                        <span className="support-ticket__reply-head">
                            <Icon name="check" size={14} />
                            <span>{t.support.replyTitle}</span>
                            {ticket.repliedAt && <span className="support-ticket__reply-when">{formatDateTime(ticket.repliedAt, lang)}</span>}
                        </span>
                        <p className="support-ticket__reply-body">{ticket.reply}</p>
                    </section>
                ) : (
                    /* Said plainly, because the alternative is a person refreshing a screen that
                       looks the same either way. There is no notification to wait for. */
                    <p className="support-ticket__waiting">{t.support.noReply}</p>
                )}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose}>
                    {t.common.close}
                </button>

                {onHide && (
                    <button
                        type="button"
                        className="support-ticket__hide"
                        onClick={() => {
                            onClose();
                            onHide();
                        }}
                    >
                        <Icon name="trash" size={15} />
                        <span>{t.support.hide}</span>
                    </button>
                )}
            </footer>
        </section>
    );
}
